/* global axios */
/**
 * The forms screen's wire — the second module in the Waslo tree that talks to CHATWOOT rather
 * than to the authoring service, and for the same reason as the first.
 *
 * WHY NOT `api/client.js`: that module is the wire to the authoring BFF at `/authoring/api/v1` —
 * a different origin path, a different auth story and a different error vocabulary. A WhatsApp
 * form lives on the Chatwoot channel row (the WABA id and the Graph token are there and nowhere
 * else), so these calls go to Chatwoot's own `/api/v1/accounts/:id/**`. Mixing them into one
 * client would mean one module with two error vocabularies, which is how a 402 about AI credits
 * ends up rendered on a screen that has nothing to do with credits.
 *
 * WHY GLOBAL `axios` AND NOT `fetch`, which `api/client.js` uses: these are Chatwoot's own
 * routes, and Chatwoot's axios instance is what carries the dashboard's auth headers
 * (`helper/APIHelper.js` sets them on `axios.defaults`). Every upstream API module in
 * `dashboard/api/` is written this way.
 *
 * `senders` DELEGATES to `api/templates.js` rather than repeating it. Both screens need the same
 * list — the account's WhatsApp Cloud inboxes — and two implementations of "which numbers can do
 * this" would drift the moment one of them learns about a new provider. The templates module is
 * where that list already lives.
 *
 * ── THE ERROR VOCABULARY, AND WHY IT HAS TWO LAYERS ─────────────────────────────────
 *
 * The HTTP status says what KIND of failure it was; the server's own `errors[0].code` says WHICH
 * one. Both are carried, because the screen's sentence depends on the second: a 422 can mean the
 * number is not eligible (and the verdict rides along, so the screen can name the reason), or
 * that this task has no form in it at all, or that Meta refused the publish. A 503 means the form
 * builder in the sidecar is absent or refusing — which is the feature's state TODAY, and it must
 * read as "this part is not switched on yet" rather than as a generic failure.
 */

export const FLOW_ERROR_CODES = {
  NETWORK: 'network',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  REJECTED: 'rejected',
  UNAVAILABLE: 'unavailable',
  SERVER: 'server',
  MALFORMED: 'malformed',
};

export class FlowApiError extends Error {
  constructor(
    message,
    {
      status = 0,
      code,
      serverCode = null,
      errors = null,
      metaError = null,
      eligibility = null,
      form = null,
    } = {}
  ) {
    super(message);
    this.name = 'FlowApiError';
    this.status = status;
    this.code = code ?? FLOW_ERROR_CODES.SERVER;
    /** The server's own word for it — `not_eligible`, `no_form`, `builder_refused`, … */
    this.serverCode = serverCode;
    this.errors = errors;
    this.metaError = metaError;
    /** Present on a `not_eligible` refusal, so the screen can say WHICH signal is wrong. */
    this.eligibility = eligibility;
    /** Present on a `no_form` refusal: the steps that would have stayed in the chat. */
    this.form = form;
  }
}

const codeForStatus = status => {
  if (status === 401) return FLOW_ERROR_CODES.UNAUTHORIZED;
  if (status === 403) return FLOW_ERROR_CODES.FORBIDDEN;
  if (status === 404) return FLOW_ERROR_CODES.NOT_FOUND;
  if (status === 422 || status === 400) return FLOW_ERROR_CODES.REJECTED;
  if (status === 503) return FLOW_ERROR_CODES.UNAVAILABLE;
  if (status === 0 || status === undefined) return FLOW_ERROR_CODES.NETWORK;
  return FLOW_ERROR_CODES.SERVER;
};

const wrap = error => {
  if (error instanceof FlowApiError) return error;
  const status = error?.response?.status ?? 0;
  const data = error?.response?.data ?? {};
  const errors = Array.isArray(data.errors) ? data.errors : null;
  return new FlowApiError(error?.message ?? 'request failed', {
    status: status,
    code: codeForStatus(status),
    serverCode: errors?.[0]?.code ?? null,
    errors: errors,
    metaError: data.meta_error ?? null,
    eligibility: data.eligibility ?? null,
    form: data.form ?? null,
  });
};

const accountBase = accountId => `/api/v1/accounts/${accountId}`;
const flowsBase = (accountId, inboxId) =>
  `${accountBase(accountId)}/waslo/inboxes/${inboxId}/flows`;

const text = value => (typeof value === 'string' && value ? value : null);

/**
 * ONE VERDICT, READ APART.
 *
 * `eligible` is the only boolean the screen may act on, and it is read STRICTLY — the server has
 * already refused to call an unrecognised verdict eligible, and this does not soften that.
 *
 * `code` is what the screen turns into a sentence, because a sentence written on the server
 * arrives in ONE language on a bilingual product. `reason` is the server's own English and is
 * carried for exactly one case: a code this build has no wording for, where quoting it beats
 * inventing a cause. The eligibility rules live in the sidecar and in no other place.
 *
 * `signals` are the health fields the verdict was read from, echoed back by the fork in its own
 * snake_case. `status` is REPORTED AND NOT DECISIVE here — it is shown beside the messaging tier
 * so an owner can see a suspended number, while the rule that acts on it lives with the others.
 */
export const narrowEligibility = raw => ({
  eligible: raw?.eligible === true,
  code: text(raw?.code) ?? 'unknown_signals',
  reason: text(raw?.reason),
  signals: {
    verifiedName: text(raw?.signals?.verified_name),
    nameStatus: text(raw?.signals?.name_status),
    qualityRating: text(raw?.signals?.quality_rating),
    status: text(raw?.signals?.status),
    messagingLimitTier: text(raw?.signals?.messaging_limit_tier),
  },
  checkedAt: text(raw?.checked_at),
});

/**
 * A row of Meta's own flow list. `status` is META'S WORD and is null when Meta did not give one —
 * never a plausible default. The sibling templates screen exists because Chatwoot got exactly
 * this wrong twice (see `api/templates.js`).
 */
export const narrowFlow = raw => ({
  id: text(raw?.id),
  name: text(raw?.name) ?? '',
  status: text(raw?.status),
  categories: Array.isArray(raw?.categories) ? raw.categories.map(String) : [],
  validationErrors: Array.isArray(raw?.validation_errors)
    ? raw.validation_errors
    : [],
  /**
   * WHICH LANGUAGE'S FORM THIS IS, read off the name by the server — the only place it is
   * recorded, because Meta stores no such field. A form is not a translation of another form:
   * the builder refuses to label a box in a language its question is not written in, so the two
   * forms of one task have different BOXES and are two artefacts with two ids.
   *
   * null for a form Waslo did not name, AND for one named before the suffix existed.
   */
  lang: text(raw?.lang),
  /**
   * One of ours, published before a form's name said which language it was.
   *
   * Strict, like `has_form`: it drives a sentence telling an owner to publish again, and a
   * shape change must not start showing that beside every form she has.
   */
  legacy: raw?.legacy === true,
});

/** What the owner sees before she publishes: the fields, and the steps that stay in the chat. */
export const narrowForm = raw => ({
  hasForm: raw?.has_form === true,
  fieldCount: Number.isFinite(raw?.field_count) ? raw.field_count : 0,
  // The sidecar's own cap on how many boxes one screen may carry. Read, never re-derived: it is
  // Waslo's number rather than a confirmed Meta limit, and it must come down in ONE place if the
  // real one turns out to be lower.
  maxFields: Number.isFinite(raw?.max_fields) ? raw.max_fields : null,
  // `label` is what the CUSTOMER will read on the box — the rendered label, after WhatsApp's
  // own per-component cap. It is read, never re-derived: the cap differs per component and the
  // generator owns the trimming, so a truncation test on this side would be a second and wrong
  // copy of it. `name` is the machine key and is on no form anywhere.
  fields: (Array.isArray(raw?.fields) ? raw.fields : []).map(field => ({
    name: text(field?.name) ?? '',
    stepId: text(field?.step_id),
    shape: text(field?.shape),
    label: text(field?.label) ?? '',
    labelTruncated: field?.label_truncated === true,
  })),
  stayInChat: (Array.isArray(raw?.stay_in_chat) ? raw.stay_in_chat : []).map(
    step => ({
      stepId: text(step?.step_id),
      name: text(step?.name) ?? '',
      reason: text(step?.reason),
    })
  ),
  definitionVersion: Number.isFinite(raw?.definition_version)
    ? raw.definition_version
    : null,
  // WHICH LANGUAGE this preview is of. The boxes differ per language, so a preview that does
  // not say is a list an owner cannot attribute to either of her two forms.
  lang: text(raw?.lang),
  name: text(raw?.name),
  // P-4.10: the pages the form is split into, as the customer turns them, and what a task edit
  // did to the owner's layout. An answer from a build before pages reads as none of either.
  pages: (Array.isArray(raw?.pages) ? raw.pages : []).flatMap(page => {
    const id = text(page?.id);
    if (!id) return [];
    return [
      {
        id,
        title: text(page?.title) ?? '',
        intro: text(page?.intro),
        fieldNames: Array.isArray(page?.field_names)
          ? page.field_names.filter(name => typeof name === 'string')
          : [],
        button: text(page?.button) ?? '',
      },
    ];
  }),
  layoutIssues: (Array.isArray(raw?.layout_issues)
    ? raw.layout_issues
    : []
  ).flatMap(issue => {
    const warning = text(issue?.warning);
    return warning
      ? [
          {
            stepId: text(issue?.step_id),
            warning,
            detail: text(issue?.detail),
          },
        ]
      : [];
  }),
});

/**
 * One form the sidecar has recorded as published on this number (P-4.8), as the flows
 * controller forwards it. The two flags are STRICT: a shape change must never make the tab call a
 * form live. `taskId` is null for a task that was deleted, switched off or has no steps.
 */
export const narrowPublished = raw => ({
  taskId: Number.isInteger(raw?.task_id) ? raw.task_id : null,
  lang: text(raw?.lang),
  definitionVersion: Number.isInteger(raw?.definition_version)
    ? raw.definition_version
    : null,
  storedVersion: Number.isInteger(raw?.stored_version)
    ? raw.stored_version
    : null,
  shapeCurrent: raw?.shape_current === true,
  textCurrent: raw?.text_current === true,
  flowId: text(raw?.flow_id),
  updatedAt: text(raw?.updated_at),
});

/**
 * The list read's body. Throws MALFORMED on a body with no list.
 *
 * `complete` (field report F3.2) is false only when the controller says the list stopped before
 * Meta's last page (`flows_complete: false`). A controller older than that says nothing, which
 * reads as complete: that is what its single page always claimed.
 */
export const readFlowList = data => {
  if (!data || typeof data !== 'object' || !Array.isArray(data.flows)) {
    throw new FlowApiError('no flow list in the answer', {
      code: FLOW_ERROR_CODES.MALFORMED,
    });
  }
  return {
    // NULL WHEN THE VERDICT COULD NOT BE OBTAINED, never a default that reads as a pass.
    // `builderError` says which refusal it was, so the screen can say "we could not check"
    // rather than showing a number as ready or as blocked on no evidence.
    eligibility: data.eligibility ? narrowEligibility(data.eligibility) : null,
    builderError: text(data.builder_error),
    flows: data.flows.map(narrowFlow),
    complete: data.flows_complete !== false,
    metaError: data.meta_error ?? null,
    // P-4.8. NULL WHEN IT COULD NOT BE READ: the tab then says so and offers no Publish.
    publishedForms: Array.isArray(data.published)
      ? data.published.map(narrowPublished)
      : null,
  };
};

export const flowsApi = {
  /** Delegated on purpose — see the header. */
  async senders(accountId, signal) {
    const { templatesApi } = await import('./templates.js');
    return templatesApi.senders(accountId, signal);
  },

  /**
   * The verdict and Meta's list in one read. Both, always: an owner whose rating has slipped
   * needs to see the refusal AND the forms she published while it was green.
   */
  async list(accountId, inboxId, signal) {
    try {
      const { data } = await axios.get(flowsBase(accountId, inboxId), {
        signal: signal,
      });
      return readFlowList(data);
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * `task` is an OPAQUE IDENTIFIER. The sidecar accepts either its own machine name or the numeric
   * task id, and this screen sends the id — the authoring BFF invents the machine name and
   * deliberately never ships it (`api/client.js:1108`, `:2605`), so the id is the only stable
   * identifier the browser has. Nothing on either side of this call slugifies anything.
   */
  async preview(accountId, inboxId, task, lang, signal) {
    try {
      const { data } = await axios.get(
        `${flowsBase(accountId, inboxId)}/preview`,
        { params: { task: task, lang: lang }, signal: signal }
      );
      if (!data?.form || typeof data.form !== 'object') {
        throw new FlowApiError('no form in the answer', {
          code: FLOW_ERROR_CODES.MALFORMED,
        });
      }
      return narrowForm(data.form);
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * NO STATUS IS INVENTED HERE EITHER. The server answers with the flow's id and nothing about
   * its state, because Meta's publish call says nothing about its state. The screen re-reads
   * `list` to learn Meta's own word for it.
   */
  async publish(accountId, inboxId, task, lang) {
    try {
      const { data } = await axios.post(flowsBase(accountId, inboxId), {
        task: task,
        lang: lang,
      });
      const flow = data?.flow;
      if (!flow || typeof flow !== 'object') {
        throw new FlowApiError('no flow in the answer', {
          code: FLOW_ERROR_CODES.MALFORMED,
        });
      }
      return {
        id: text(flow.id),
        name: text(flow.name) ?? '',
        lang: text(flow.lang),
        definitionVersion: Number.isFinite(flow.definition_version)
          ? flow.definition_version
          : null,
        /**
         * WHETHER A CUSTOMER WILL EVER SEE THIS FORM.
         *
         * Meta published it; that is `id`. Whether the assistant can OPEN it is a different
         * fact, and it was the silent half of this feature for its whole life: the id has to
         * reach the builder, and it does not when the call failed, when something newer is
         * already recorded, or when the task was edited mid-publish.
         *
         * Strict, and `recordError` says which. A form that is not sendable is not an error —
         * it exists and it is listed — so the screen says it plainly and the owner publishes
         * again, which is idempotent.
         */
        sendable: flow.sendable === true,
        recordError: text(flow.record_error),
        status: text(flow.status),
        validationErrors: Array.isArray(flow.validation_errors)
          ? flow.validation_errors
          : [],
        stayInChat: (Array.isArray(flow.stay_in_chat)
          ? flow.stay_in_chat
          : []
        ).map(step => ({
          stepId: text(step?.step_id),
          name: text(step?.name) ?? '',
          reason: text(step?.reason),
        })),
      };
    } catch (error) {
      throw wrap(error);
    }
  },

  /**
   * WhatsApp's own preview link for ONE published form (P-4.8). Read only when she presses
   * "Try it": the link is short-lived and Meta mints it on request.
   */
  async formPreview(accountId, inboxId, flowId) {
    try {
      const { data } = await axios.get(
        `${flowsBase(accountId, inboxId)}/${encodeURIComponent(flowId)}/preview`
      );
      const url = text(data?.preview_url);
      if (!url || !/^https:\/\//.test(url)) {
        throw new FlowApiError('no preview link in the answer', {
          code: FLOW_ERROR_CODES.MALFORMED,
        });
      }
      return { previewUrl: url, expiresAt: text(data?.expires_at) };
    } catch (error) {
      throw wrap(error);
    }
  },
};

export default flowsApi;
